
import { useEffect, useState } from 'react';
import { useIsMobile } from './use-mobile';

export function useAnimationSimplifier() {
  const isMobile = useIsMobile();
  const [devicePerformanceTier, setDevicePerformanceTier] = useState<'low' | 'medium' | 'high'>('medium');
  const [mobileSafeFor3D, setMobileSafeFor3D] = useState(false);
  const [screenSize, setScreenSize] = useState<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [hasWebGLSupport, setHasWebGLSupport] = useState(true);
  
  // Configure animation settings based on device performance and preferences
  const shouldSimplifyAnimations = devicePerformanceTier === 'low' || 
    (isMobile && devicePerformanceTier !== 'high') || 
    isSafari;
  
  // Should completely disable heavy animations like 3D scenes
  const shouldDisableHeavyAnimations = devicePerformanceTier === 'low' || 
    (isSafari && !hasWebGLSupport);
  
  // Animation duration modifier (shorter on mobile)
  const animationDuration = isMobile ? 0.5 : 0.7;

  // Detect device capabilities on mount
  useEffect(() => {
    // Check if it's a touch device
    const touchDevice = ('ontouchstart' in window) || 
      navigator.maxTouchPoints > 0;
    setIsTouchDevice(touchDevice);
    
    // Detect Safari browser
    const userAgent = navigator.userAgent.toLowerCase();
    // Check for Safari (but not Chrome, as Chrome on iOS reports itself as Safari too)
    const isSafariBrowser = userAgent.indexOf('safari') !== -1 && userAgent.indexOf('chrome') === -1;
    setIsSafari(isSafariBrowser);
    
    // Determine screen size
    const determineScreenSize = () => {
      const width = window.innerWidth;
      if (width < 640) return 'xs';
      if (width < 768) return 'sm';
      if (width < 1024) return 'md';
      if (width < 1280) return 'lg';
      return 'xl';
    };
    
    setScreenSize(determineScreenSize());
    
    // Listen for window resize to update screen size
    const handleResize = () => {
      setScreenSize(determineScreenSize());
    };
    
    window.addEventListener('resize', handleResize);
    
    // Performance detection
    const detectDevicePerformance = async () => {
      // Start with mediocre assumption
      let performanceTier: 'low' | 'medium' | 'high' = 'medium';
      let webglSupport = true;
      let safeForMobile3D = false;
      
      try {
        // Check WebGL capabilities
        const canvas = document.createElement('canvas');
        // Use type assertion to specify that we're expecting a WebGL context
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
        
        if (!gl) {
          performanceTier = 'low';
          webglSupport = false;
        } else {
          // Check for various WebGL capabilities
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          
          if (debugInfo) {
            try {
              const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
              const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
              
              // Check for low-end GPU indicators
              const isLowEnd = 
                /intel|hd graphics|llvmpipe|swiftshader|lavapipe/i.test(renderer) ||
                (isMobile && /mali-4|adreno 3|powervr/i.test(renderer));
                
              // Check for high-end GPU indicators  
              const isHighEnd = 
                /nvidia|radeon|adreno 6|adreno 7|mali-g7|apple m1|apple m2/i.test(renderer) ||
                /rtx|gtx 16|gtx 10|rx 5|rx 6|rx 7|a100|a6000/i.test(renderer);
              
              // Special handling for Apple devices which often have capable GPUs
              const isAppleGPU = /apple/i.test(vendor) || /apple/i.test(renderer);
              
              if (isLowEnd) {
                performanceTier = 'low';
              } else if (isHighEnd || isAppleGPU) {
                performanceTier = 'high';
              }
              
              // Check if mobile device is capable of 3D
              if (isMobile) {
                // These mobile GPUs can handle 3D content reasonably well
                const goodMobileGPUs = /adreno 6|adreno 7|mali-g7|mali-g8|apple gpu/i;
                safeForMobile3D = goodMobileGPUs.test(renderer) || isAppleGPU;
              } else {
                // Desktop devices are generally safe for 3D unless very old
                safeForMobile3D = true;
              }
            } catch (error) {
              // Safari in private browsing or with restricted permissions 
              // will throw an error when trying to access renderer info
              console.log("Could not access renderer info - possible Safari privacy restrictions");
              
              // For Safari, we make an educated guess based on device
              if (isSafariBrowser) {
                // Modern Safari browsers typically run on capable hardware
                performanceTier = isMobile ? 'medium' : 'high';
                safeForMobile3D = !isMobile || /iphone/i.test(userAgent) && !(/iphone [1-8]/i.test(userAgent));
                
                // Check if WebGL works at all
                const textureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
                webglSupport = textureSize >= 4096; // Modern devices support at least 4k textures
              }
            }
          } else {
            // If debug info isn't available (like in Safari private browsing)
            console.log("WebGL debug info not available");
            
            // For Safari, use device characteristics as proxy for performance
            if (isSafariBrowser) {
              // Check basic WebGL capabilities as proxy for performance
              const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
              const maxRenderBufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE);
              
              if (maxTextureSize >= 8192 && maxRenderBufferSize >= 8192) {
                performanceTier = 'high';
              } else if (maxTextureSize >= 4096 && maxRenderBufferSize >= 4096) {
                performanceTier = 'medium';
              } else {
                performanceTier = 'low';
              }
              
              // Most modern Safari instances can handle basic 3D
              safeForMobile3D = !isMobile || performanceTier !== 'low';
              webglSupport = true;
            }
          }
        }
      } catch (error) {
        console.error("Error detecting device performance:", error);
        // Fallback to medium tier on error
        performanceTier = isSafariBrowser ? 'medium' : 'low';
        safeForMobile3D = !isMobile; // Assume only desktop is safe on error
        webglSupport = !isSafariBrowser;
      }
      
      // Set the determined performance tier
      setDevicePerformanceTier(performanceTier);
      setMobileSafeFor3D(safeForMobile3D);
      setHasWebGLSupport(webglSupport);
    };

    detectDevicePerformance();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);
  
  return {
    devicePerformanceTier,
    mobileSafeFor3D,
    shouldSimplifyAnimations,
    shouldDisableHeavyAnimations,
    animationDuration,
    isMobile,
    screenSize,
    isTouchDevice,
    isSafari,
    hasWebGLSupport
  };
}
